// src/App.tsx
import { Suspense, lazy, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { LanguageWrapper } from '@/components/LanguageRouter'
import { Layout } from '@/components/layout'
import { CookieConsentProvider } from '@/contexts/CookieConsentContext'
import { ScrollToTop } from '@/components/ScrollToTop'
import { localizedRoutes } from '@/i18n/routes'

// Lazy load pages per code splitting
const Home = lazy(() => import('@/pages/Home'))
const About = lazy(() => import('@/pages/About'))
const Breweries = lazy(() => import('@/pages/Breweries'))
const BreweryDetail = lazy(() => import('@/pages/BreweryDetail'))
const News = lazy(() => import('@/pages/News'))
const NewsArticle = lazy(() => import('@/pages/NewsArticle'))
const Contacts = lazy(() => import('@/pages/Contacts'))
const WorkWithUs = lazy(() => import('@/pages/WorkWithUs'))
const Privacy = lazy(() => import('@/pages/Privacy'))
const Whistleblowing = lazy(() => import('@/pages/Whistleblowing'))
const Accessibility = lazy(() => import('@/pages/Accessibility'))
const ReservedArea = lazy(() => import('@/pages/ReservedArea'))
const NotFound = lazy(() => import('@/pages/NotFound'))

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
    </div>
  )
}

function SuspensePage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

/** Collect all unique path slugs for a route key across all languages */
function allPathsFor(routeKey: keyof typeof localizedRoutes): string[] {
  const routes = localizedRoutes[routeKey]
  const unique = new Set<string>()
  for (const path of Object.values(routes)) {
    if (path) unique.add(path)
  }
  return [...unique]
}

export default function App() {
  return (
    <CookieConsentProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
        {/* Redirect root to default language */}
        <Route path="/" element={<Navigate to="/it" replace />} />

        {/* Language prefixed routes */}
        <Route path="/:lang/*" element={<LanguageWrapper />}>
          <Route element={<Layout />}>
            {/* Home */}
            <Route index element={<SuspensePage><Home /></SuspensePage>} />

            {/* About */}
            {allPathsFor('about').map(p => (
              <Route key={p} path={p} element={<SuspensePage><About /></SuspensePage>} />
            ))}

            {/* Breweries */}
            {allPathsFor('breweries').map(p => (
              <Route key={p} path={p} element={<SuspensePage><Breweries /></SuspensePage>} />
            ))}
            {allPathsFor('breweries').map(p => (
              <Route key={`${p}/:id`} path={`${p}/:id`} element={<SuspensePage><BreweryDetail /></SuspensePage>} />
            ))}

            {/* News */}
            {allPathsFor('news').map(p => (
              <Route key={p} path={p} element={<SuspensePage><News /></SuspensePage>} />
            ))}
            {allPathsFor('news').map(p => (
              <Route key={`${p}/:slug`} path={`${p}/:slug`} element={<SuspensePage><NewsArticle /></SuspensePage>} />
            ))}

            {/* Contacts */}
            {allPathsFor('contacts').map(p => (
              <Route key={p} path={p} element={<SuspensePage><Contacts /></SuspensePage>} />
            ))}

            {/* Work With Us */}
            {allPathsFor('work').map(p => (
              <Route key={p} path={p} element={<SuspensePage><WorkWithUs /></SuspensePage>} />
            ))}

            {/* Privacy */}
            {allPathsFor('privacy').map(p => (
              <Route key={p} path={p} element={<SuspensePage><Privacy /></SuspensePage>} />
            ))}

            {/* Whistleblowing */}
            {allPathsFor('whistleblowing').map(p => (
              <Route key={p} path={p} element={<SuspensePage><Whistleblowing /></SuspensePage>} />
            ))}

            {/* Accessibility */}
            {allPathsFor('accessibility').map(p => (
              <Route key={p} path={p} element={<SuspensePage><Accessibility /></SuspensePage>} />
            ))}

            {/* Reserved Area */}
            {allPathsFor('reservedArea').map(p => (
              <Route key={p} path={p} element={<SuspensePage><ReservedArea /></SuspensePage>} />
            ))}

            {/* 404 */}
            <Route path="*" element={<SuspensePage><NotFound /></SuspensePage>} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
    </CookieConsentProvider>
  )
}
